'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Film, Camera, Sparkles, Scissors, ArrowRight, Check, Clock, Users, Zap } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface Service {
  id: string;
  icon: React.ElementType;
  title: string;
  tagline: string;
  description: string;
  capabilities: string[];
  deliverables: string[];
  turnaround: string;
  teamSize: string;
  highlight: string;
  color: string;
  borderColor: string;
  iconBg: string;
}

const services: Service[] = [
  {
    id: 'video',
    icon: Film,
    title: 'Video Production',
    tagline: 'Cinematic storytelling at scale',
    description:
      'End-to-end video production from concept development through final delivery. We handle pre-production, full crew deployment, and post — so you get a finished asset, not a raw file.',
    capabilities: [
      'Brand films & anthems',
      'Commercial & TVC production',
      'Documentary & long-form',
      'Social content at volume',
      '4K/6K/8K capture',
      'Drone & aerial unit',
      'Multi-camera live events',
      'Scriptwriting & storyboarding',
    ],
    deliverables: ['Master file (ProRes/H.265)', 'Platform-cut edits', 'Thumbnail assets', 'Raw footage archive'],
    turnaround: '2–6 weeks',
    teamSize: '4–18 crew',
    highlight: '8K-capable production unit',
    color: 'from-amber-500/8',
    borderColor: 'border-amber-500/20',
    iconBg: 'bg-amber-500/10 border-amber-500/20',
  },
  {
    id: 'onsite',
    icon: Camera,
    title: 'On-Site Shooting',
    tagline: 'Location-first, studio-quality',
    description:
      'We bring the studio to your location. Our mobile production unit is rigged for any environment — from controlled industrial facilities to remote wilderness — with zero quality compromise.',
    capabilities: [
      'Location scouting & permits',
      'Remote & international shoots',
      'Industrial & facility coverage',
      'Fashion & editorial on-location',
      'Food & hospitality photography',
      'Architecture & real estate',
      'Event & conference coverage',
      'Same-day turnaround available',
    ],
    deliverables: ['High-res RAW files', 'Edited final selection', 'Usage-ready exports', 'BTS content package'],
    turnaround: '1–3 weeks',
    teamSize: '2–8 crew',
    highlight: 'Operates in 40+ countries',
    color: 'from-emerald-500/8',
    borderColor: 'border-emerald-500/20',
    iconBg: 'bg-emerald-500/10 border-emerald-500/20',
  },
  {
    id: 'ai',
    icon: Sparkles,
    title: 'AI-Enhanced Projects',
    tagline: 'Human creativity, machine precision',
    description:
      'We integrate AI tools at every stage — from AI-generated concept art and storyboards to synthetic voiceover, motion generation, and upscaling — to accelerate production without sacrificing craft.',
    capabilities: [
      'AI concept & moodboard generation',
      'Synthetic voiceover & dubbing',
      'AI motion graphics & VFX',
      'Footage upscaling (up to 8K)',
      'Automated subtitle generation',
      'AI color grading assistance',
      'Scene generation & compositing',
      'Multilingual AI localization',
    ],
    deliverables: ['AI-enhanced master', 'Localized versions', 'Motion asset pack', 'Prompt & workflow docs'],
    turnaround: '3–10 days',
    teamSize: '1–4 specialists',
    highlight: 'Proprietary AI pipeline',
    color: 'from-violet-500/8',
    borderColor: 'border-violet-500/20',
    iconBg: 'bg-violet-500/10 border-violet-500/20',
  },
  {
    id: 'editing',
    icon: Scissors,
    title: 'Post-Production & Editing',
    tagline: 'Where raw footage becomes story',
    description:
      'Our post team handles everything after the shoot — from assembly cut through color grade, sound design, motion titles, and final QC. We work in your NLE or ours.',
    capabilities: [
      'Offline & online editing',
      'DaVinci Resolve color grading',
      'Dolby Atmos sound design',
      'Motion graphics & titles',
      'VFX compositing',
      'Format delivery (DCP, HDR, SDR)',
      'Series & episodic post',
      'Subtitling & closed captions',
    ],
    deliverables: ['Graded master', 'Broadcast-ready deliverables', 'Social cuts', 'Audio stems'],
    turnaround: '1–4 weeks',
    teamSize: '2–6 specialists',
    highlight: 'Dolby-certified facility',
    color: 'from-rose-500/8',
    borderColor: 'border-rose-500/20',
    iconBg: 'bg-rose-500/10 border-rose-500/20',
  },
];

function ServiceCard({ service, index }: { service: Service; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const Icon = service.icon;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group relative rounded-2xl bg-gradient-to-br ${service.color} to-transparent bg-studio-900 border ${service.borderColor} overflow-hidden transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Shine overlay */}
      <div className="absolute inset-0 bg-card-shine pointer-events-none" />

      <div className="relative p-6 xl:p-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className={`w-12 h-12 rounded-xl border ${service.iconBg} flex items-center justify-center shrink-0`}>
            <Icon size={22} className="text-current opacity-80" />
          </div>
          <div className="shrink-0 flex items-center gap-1.5 px-2.5 py-1 bg-studio-800/60 border border-studio-700/40 rounded-full">
            <Zap size={10} className="text-gold-400" />
            <span className="text-studio-300 text-[10px] font-medium">{service.highlight}</span>
          </div>
        </div>

        <h3 className="font-display font-bold text-xl text-white mb-1">{service.title}</h3>
        <p className="text-gold-400 text-xs font-medium tracking-wide mb-3">{service.tagline}</p>
        <p className="text-studio-400 text-sm leading-relaxed mb-6">{service.description}</p>

        {/* Meta row */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-1.5">
            <Clock size={12} className="text-studio-500" />
            <span className="text-studio-400 text-xs">{service.turnaround}</span>
          </div>
          <div className="w-px h-3 bg-studio-700" />
          <div className="flex items-center gap-1.5">
            <Users size={12} className="text-studio-500" />
            <span className="text-studio-400 text-xs">{service.teamSize}</span>
          </div>
        </div>

        {/* Capabilities */}
        <div className="mb-5">
          <p className="text-studio-400 text-xs font-semibold tracking-[0.1em] uppercase mb-3">Capabilities</p>
          <div className="grid grid-cols-1 gap-1.5">
            {service.capabilities.slice(0, expanded ? service.capabilities.length : 4).map((cap) => (
              <div key={cap} className="flex items-center gap-2">
                <Check size={11} className="text-gold-400 shrink-0" />
                <span className="text-studio-300 text-sm">{cap}</span>
              </div>
            ))}
          </div>
          {service.capabilities.length > 4 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-3 text-gold-400 text-xs font-medium hover:text-gold-300 transition-colors flex items-center gap-1"
            >
              {expanded ? 'Show less' : `+${service.capabilities.length - 4} more`}
            </button>
          )}
        </div>

        {/* Deliverables */}
        <div className="mb-6 p-3 bg-studio-950/50 rounded-xl border border-studio-700/30">
          <p className="text-studio-500 text-[10px] font-semibold tracking-[0.1em] uppercase mb-2">Deliverables</p>
          <div className="flex flex-wrap gap-1.5">
            {service.deliverables.map((d) => (
              <span key={d} className="px-2 py-0.5 bg-studio-800/60 border border-studio-700/30 rounded text-studio-300 text-[11px]">
                {d}
              </span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <button
          className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition-all duration-200 group/btn active:scale-95 ${service.borderColor} text-studio-200 hover:text-white hover:bg-studio-700/40`}
          onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
        >
          Enquire About This Service
          <ArrowRight size={14} className="group-hover/btn:translate-x-0.5 transition-transform duration-200" />
        </button>
      </div>
    </div>
  );
}

export default function ServicesSection() {
  return (
    <section id="services" className="py-24 xl:py-32 scroll-mt-20 bg-studio-950" aria-label="Our services">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-12 2xl:px-16">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-gold-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">
            What We Do
          </p>
          <h2 className="font-display font-bold text-4xl md:text-5xl xl:text-6xl text-white leading-tight mb-5">
            Full-Spectrum
            <br />
            <span className="gradient-text-gold">Creative Services</span>
          </h2>
          <p className="text-studio-400 text-base leading-relaxed">
            Four service lines, one creative vision. Whether you need a 60-second social asset or a 6-episode docuseries, NASARC handles every stage with the same obsessive craft.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 xl:gap-6">
          {services.map((service, i) => (
            <ServiceCard key={service.id} service={service} index={i} />
          ))}
        </div>

        {/* Process strip */}
        <div className="mt-16 p-6 xl:p-8 bg-studio-900 rounded-2xl border border-studio-700/50">
          <p className="text-studio-400 text-xs font-semibold tracking-[0.15em] uppercase mb-6 text-center">
            How Every Project Runs
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 relative">
            {[
              { step: '01', label: 'Discovery', desc: 'Brief, goals, budget' },
              { step: '02', label: 'Concept', desc: 'Treatment & moodboards' },
              { step: '03', label: 'Production', desc: 'Shoot or AI generation' },
              { step: '04', label: 'Post', desc: 'Edit, grade, sound' },
              { step: '05', label: 'Delivery', desc: 'Files + archive' },
            ].map((item, i) => (
              <div key={item.step} className="flex flex-col items-center text-center relative">
                {i < 4 && (
                  <div className="absolute top-4 left-[calc(50%+20px)] right-0 h-px bg-studio-700/60 hidden md:block" />
                )}
                <div className="w-9 h-9 rounded-full bg-gold-500/10 border border-gold-500/25 flex items-center justify-center mb-3 relative z-10">
                  <span className="text-gold-400 text-xs font-bold font-mono-data">{item.step}</span>
                </div>
                <p className="text-white text-sm font-semibold mb-0.5">{item.label}</p>
                <p className="text-studio-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}